import { isNull, isNumber, isUndefined } from 'lodash';
import { EStorageKeys } from '../types/enums';

export const NB_FACILITY_ID_PARAM = 'nbFacilityId';
export const ALL_FACILITIES_PARAM_VALUE = 0;

const BACKOFFICE_CLIENT_PATH = '/client';
const BACKOFFICE_CLIENT_PATH_PREFIX = `${BACKOFFICE_CLIENT_PATH}/`;
const BLANK_TARGET = '_blank';
const URI_LIST_DRAG_DATA_TYPE = 'text/uri-list';
const PLAIN_TEXT_DRAG_DATA_TYPE = 'text/plain';

enum LinkIntentEventEnum {
	CLICK = 'click',
	AUX_CLICK = 'auxclick',
	CONTEXT_MENU = 'contextmenu',
	DRAG_START = 'dragstart',
}

type StoredActiveFacilityId = number | null | undefined;

let isFacilityLinkInterceptorInstalled = false;
let pendingFacilityIdFromUrl: StoredActiveFacilityId;
let pendingFacilityIdClearTimeoutId: number | undefined;

/**
 * Reads the persisted active facility preference from session storage.
 *
 * @param value - Raw session storage value for the active facility preference.
 * @returns Parsed facility id, null for All Facilities, or undefined when no valid preference exists.
 */
function parseStoredActiveFacilityId(value: string | null): StoredActiveFacilityId {
	if (isNull(value)) return undefined;
	try {
		const parsed: unknown = JSON.parse(value);
		if (isNull(parsed)) return null;
		if (isNumber(parsed) && Number.isInteger(parsed) && parsed > ALL_FACILITIES_PARAM_VALUE) {
			return parsed;
		}
		return undefined;
	} catch {
		return undefined;
	}
}

/**
 * Converts the URL handoff value into an active facility preference.
 *
 * @param value - Raw nbFacilityId query parameter value.
 * @returns Parsed facility id, null for All Facilities, or undefined for invalid values.
 */
function parseFacilityIdParam(value: string | null): StoredActiveFacilityId {
	if (isNull(value) || value.trim() === '') return undefined;
	const parsed = Number(value);
	if (!Number.isInteger(parsed) || parsed < ALL_FACILITIES_PARAM_VALUE) return undefined;
	return parsed === ALL_FACILITIES_PARAM_VALUE ? null : parsed;
}

/**
 * Limits facility handoff behavior to same-origin Backoffice client routes.
 *
 * @param url - URL to evaluate for facility handoff support.
 * @returns True when the URL points to a same-origin Backoffice client route.
 */
function isBackofficeClientRoute(url: URL): boolean {
	return (
		url.origin === window.location.origin &&
		(url.pathname === BACKOFFICE_CLIENT_PATH || url.pathname.startsWith(BACKOFFICE_CLIENT_PATH_PREFIX))
	);
}

/**
 * Keeps decorated URLs relative so links stay environment agnostic.
 *
 * @param url - URL to serialize as a relative path.
 * @returns URL path, query string, and hash without origin.
 */
function getRelativeUrl(url: URL): string {
	return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Encodes a stored active facility preference for URL handoff.
 *
 * @param activeFacilityId - Stored facility preference to encode.
 * @returns Query parameter value, or null when there is no preference to encode.
 */
function getDecoratedFacilityParam(activeFacilityId: StoredActiveFacilityId): string | null {
	if (isUndefined(activeFacilityId)) return null;
	return String(activeFacilityId ?? ALL_FACILITIES_PARAM_VALUE);
}

/**
 * Finds the closest anchor for document-level event interception.
 *
 * @param target - Event target from a captured browser event.
 * @returns Closest anchor element, or null when the event is not inside a link.
 */
function getAnchorFromEventTarget(target: EventTarget | null): HTMLAnchorElement | null {
	if (!(target instanceof Element)) return null;
	return target.closest('a');
}

/**
 * Detects interactions that can open an existing Backoffice route in a new tab.
 *
 * @param anchor - Anchor element involved in the event.
 * @param event - Captured mouse event.
 * @returns True when the anchor should receive a facility handoff parameter.
 */
function shouldDecorateAnchorForEvent(anchor: HTMLAnchorElement, event: MouseEvent): boolean {
	if (anchor.hasAttribute('download')) return false;
	if (anchor.target === BLANK_TARGET) return true;
	if (event.type === LinkIntentEventEnum.CONTEXT_MENU) return true;
	if (event.type === LinkIntentEventEnum.AUX_CLICK && event.button === 1) return true;
	return event.metaKey || event.ctrlKey || event.shiftKey;
}

/**
 * Returns the current active facility preference from session storage, falling back to the
 * in-memory URL handoff so new-tab opens made before the first persistence still carry the scope.
 *
 * @returns Stored facility id, null for All Facilities, or undefined when no valid preference exists.
 */
export function getStoredActiveFacilityId(): StoredActiveFacilityId {
	const stored = parseStoredActiveFacilityId(window.sessionStorage.getItem(EStorageKeys.ACTIVE_FACILITY_ID));
	return isUndefined(stored) ? pendingFacilityIdFromUrl : stored;
}

/**
 * Adds the active facility preference to same-origin Backoffice client links.
 *
 * @param href - Link href to decorate.
 * @param activeFacilityId - Facility preference to encode into the link.
 * @returns Decorated href for supported routes, or the original href when unsupported.
 */
export function withFacility(href: string, activeFacilityId: StoredActiveFacilityId): string {
	const facilityParam = getDecoratedFacilityParam(activeFacilityId);
	if (isNull(facilityParam)) return href;

	try {
		const url = new URL(href, window.location.href);
		if (!isBackofficeClientRoute(url)) return href;
		url.searchParams.set(NB_FACILITY_ID_PARAM, facilityParam);
		return getRelativeUrl(url);
	} catch {
		return href;
	}
}

/**
 * Removes the temporary facility handoff parameter from Backoffice client links.
 *
 * @param href - Link href to strip.
 * @returns Href without nbFacilityId when the route is supported, otherwise the original href.
 */
function stripFacilityParam(href: string): string {
	try {
		const url = new URL(href, window.location.href);
		if (!isBackofficeClientRoute(url) || !url.searchParams.has(NB_FACILITY_ID_PARAM)) return href;
		url.searchParams.delete(NB_FACILITY_ID_PARAM);
		return getRelativeUrl(url);
	} catch {
		return href;
	}
}

/**
 * Removes a temporary facility handoff parameter from a live anchor element.
 *
 * @param anchor - Anchor element to normalize.
 */
function stripAnchorFacilityParam(anchor: HTMLAnchorElement): void {
	const latestHref = anchor.getAttribute('href');
	if (!latestHref) return;

	const latestStrippedHref = stripFacilityParam(latestHref);
	if (latestStrippedHref !== latestHref) {
		anchor.setAttribute('href', latestStrippedHref);
	}
}

/**
 * Defers cleanup until the browser has consumed the current link event.
 *
 * @param anchor - Anchor element to clean after the current event turn.
 */
function scheduleAnchorFacilityParamStrip(anchor: HTMLAnchorElement): void {
	window.setTimeout(() => stripAnchorFacilityParam(anchor), 0);
}

/**
 * Captures and strips the one-time facility handoff parameter from the current URL.
 */
export function captureNbFacilityIdFromUrl(): void {
	const url = new URL(window.location.href);
	if (!isBackofficeClientRoute(url)) return;

	const paramValue = url.searchParams.get(NB_FACILITY_ID_PARAM);
	if (isNull(paramValue)) return;

	clearPendingFacilityIdFromUrl();
	const facilityId = parseFacilityIdParam(paramValue);
	if (!isUndefined(facilityId)) {
		pendingFacilityIdFromUrl = facilityId;
	}
	url.searchParams.delete(NB_FACILITY_ID_PARAM);
	window.history.replaceState(window.history.state, document.title, getRelativeUrl(url));
}

/**
 * Drops the in-memory URL facility handoff immediately, including a pending deferred clear.
 */
export function clearPendingFacilityIdFromUrl(): void {
	if (!isUndefined(pendingFacilityIdClearTimeoutId)) {
		window.clearTimeout(pendingFacilityIdClearTimeoutId);
		pendingFacilityIdClearTimeoutId = undefined;
	}
	pendingFacilityIdFromUrl = undefined;
}

/**
 * Clears the captured URL facility preference after mounted hooks can observe it.
 */
export function schedulePendingFacilityIdFromUrlClear(): void {
	if (!isUndefined(pendingFacilityIdClearTimeoutId)) return;

	pendingFacilityIdClearTimeoutId = window.setTimeout(() => {
		pendingFacilityIdFromUrl = undefined;
		pendingFacilityIdClearTimeoutId = undefined;
	}, 0);
}

/**
 * Returns the captured URL facility preference without consuming it.
 *
 * @returns Pending URL facility id, null for All Facilities, or undefined when absent.
 */
export function peekPendingFacilityIdFromUrl(): StoredActiveFacilityId {
	return pendingFacilityIdFromUrl;
}

/**
 * Opens a Backoffice link in a new tab with the active facility preference attached.
 *
 * @param href - Link href to open.
 */
export function openInNewTab(href: string): void {
	// eslint-disable-next-line no-restricted-properties
	const newTab = window.open(withFacility(href, getStoredActiveFacilityId()), BLANK_TARGET);
	if (newTab) newTab.opener = null;
}

/**
 * Installs global link interception for facility-aware new-tab navigation.
 */
export function installFacilityLinkInterceptor(): void {
	if (isFacilityLinkInterceptorInstalled) return;

	/**
	 * Temporarily decorates anchors before browser new-tab navigation consumes them.
	 *
	 * @param event - Captured mouse event from a possible new-tab interaction.
	 */
	const handleNewTabIntent = (event: MouseEvent) => {
		try {
			const anchor = getAnchorFromEventTarget(event.target);
			if (!anchor) return;

			const currentHref = anchor.getAttribute('href');
			if (!currentHref) return;

			if (!shouldDecorateAnchorForEvent(anchor, event)) {
				const strippedHref = stripFacilityParam(currentHref);
				if (strippedHref !== currentHref) {
					anchor.setAttribute('href', strippedHref);
				}
				return;
			}

			const strippedHref = stripFacilityParam(currentHref);
			const decoratedHref = withFacility(strippedHref, getStoredActiveFacilityId());
			if (decoratedHref !== currentHref) {
				anchor.setAttribute('href', decoratedHref);
			}

			if (event.type !== LinkIntentEventEnum.CONTEXT_MENU) {
				scheduleAnchorFacilityParamStrip(anchor);
			}
		} catch {
			return;
		}
	};

	/**
	 * Refreshes anchor drag payloads so stale context-menu hrefs cannot leak.
	 *
	 * @param event - Captured drag event from an anchor drag.
	 */
	const handleAnchorDragStart = (event: DragEvent) => {
		try {
			const anchor = getAnchorFromEventTarget(event.target);
			if (!anchor) return;

			const currentHref = anchor.getAttribute('href');
			if (!currentHref) return;

			const strippedHref = stripFacilityParam(currentHref);
			const decoratedHref = withFacility(strippedHref, getStoredActiveFacilityId());
			if (decoratedHref === currentHref && strippedHref === currentHref) return;

			const absoluteUrl = new URL(decoratedHref, window.location.href).toString();
			event.dataTransfer?.setData(URI_LIST_DRAG_DATA_TYPE, absoluteUrl);
			event.dataTransfer?.setData(PLAIN_TEXT_DRAG_DATA_TYPE, absoluteUrl);

			if (decoratedHref !== currentHref) {
				anchor.setAttribute('href', decoratedHref);
			}
			scheduleAnchorFacilityParamStrip(anchor);
		} catch {
			return;
		}
	};

	document.addEventListener(LinkIntentEventEnum.CLICK, handleNewTabIntent, true);
	document.addEventListener(LinkIntentEventEnum.AUX_CLICK, handleNewTabIntent, true);
	document.addEventListener(LinkIntentEventEnum.CONTEXT_MENU, handleNewTabIntent, true);
	document.addEventListener(LinkIntentEventEnum.DRAG_START, handleAnchorDragStart, true);
	isFacilityLinkInterceptorInstalled = true;
}
